import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import styles from "../../form.module.css";
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  DatePicker,
  Typography,
  Table,
  Spin,
  Checkbox,
} from "antd";
import type { TableProps } from "antd";
import useFetch from "../../../../hooks/useFetch";
import useToast from "../../../../hooks/useToast";
import {
  eAnswerType,
  eDocType,
  eFORM_FOR,
  eFORMSTATUS,
  eResultCode,
  eSTAGE,
  eUSER_FORM_TYPE,
  eYES_OR_NO,
  INPUT_LENGTH,
} from "../../../../utils/enum";
import { EXAM_INSTITUTE_LIST } from "../../../../utils/constants";
import { useVedOptions } from "../../../../hooks/useVedOptions";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import Text, { LanguageContext } from "../../../../context/language-context";
import { FaCheck } from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserContext } from "../../../../context";
import {
  GetFormData,
  GetSpecificUserData,
  getTimeline,
  updateField,
  UploadDocs,
} from "../../../../utils/api.constant";
import AuthUtil from "../../../../utils/auth";
import { TFormProps } from "../../../../types";
import { LoaderContext } from "../../../../context/loader";
import EncryptUtils from "../../../../utils/encrypt";
import dayjs from "dayjs";
import Utils from "../../../../utils";
import {
  allowOnlyNumericInput_onKeyDown,
  disableFutureDates_on_input_date_max,
  disableFutureDates_on_input_month_max,
  numberInputRules,
  validateFile,
} from "../../../../utils/input.validator";
import { Regex } from "../../../../utils/validation";
import { CheckCircleOutlined, CloseOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { stringDateInLanguage } from "../../../../utils/helperFunctions";
import { Rule } from "antd/es/form";
import { CURRENT_YEAR, INDIAN_STATES } from "../../../../utils/constants";
import { useFormAutoSave } from "../../../../hooks/useFormAutoSave";
import { DocumentUploadField } from "../../../../components/document-upload";
import FormHelpTooltip from "../../../../components/common/FormHelpTooltip";

const formItemLayout = {
  labelCol: {
    span: 24,
  },
  wrapperCol: {
    span: 24,
  },
};

interface DataType {
  key: string;
  label: string;
  value: JSX.Element | string;
}

// Define all Date field keys for this form to enable robust date conversion
const DATE_FIELD_KEYS = [
  "TEACHER_2026_PT1_Q4", // DOB
  "TEACHER_2026_PT1_Q18_B", // Exam Month-Year
  "TEACHER_2026_PT1_Q37", // Affidavit Date
];

// LengthCounter component - must be outside main component to avoid hooks issues
const LengthCounter: React.FC<{
  form: any;
  fieldName: string | string[];
}> = ({ form, fieldName }) => {
  // Use useWatch to get real-time updates for the field value
  const fieldValue = Form.useWatch(fieldName, form);

  return (
    <div className='length_counter'>
      {(fieldValue || "").length}/{INPUT_LENGTH.TEXTAREA_LENGTH}
    </div>
  );
};

const TeacherPartOneCurrent = ({
  disabled,
  currentFormId,
  currentUserId,
  currentFormFor,
}: TFormProps) => {
  const [form] = Form.useForm();
  const isMarried = Form.useWatch("TEACHER_2026_PT1_Q13", form);
  const isGuruRelative = Form.useWatch("TEACHER_2026_PT1_Q20_A", form);
  const isNearlyExtinct = Form.useWatch("TEACHER_2026_PT1_Q21_A", form);
  const isRelativeTaught = Form.useWatch("TEACHER_2026_PT1_Q28_YES_NO", form);
  const isInstituteOther = Form.useWatch("TEACHER_2026_PT1_Q18_D", form);

  const { post } = useFetch();
  const { onShowToast } = useToast();
  const navigate = useNavigate();
  const [formId, setFormId] = useState<string | null>(null);
  const [formFor, setFormFor] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { user } = useContext(UserContext);
  const { userLanguage, userLanguageChange, dictionary } =
    useContext(LanguageContext);
  const [userId, setUserId] = useState<string | null>(null);

  // Pre-calculate Indian State options to avoid calling Text hook inside loops
  const indianStateOptions = useMemo(() => {
    return INDIAN_STATES.map((stateKey) => ({
      key: stateKey,
      value: stateKey,
      label: dictionary[stateKey] || stateKey,
    }));
  }, [dictionary]);
  const [documentsData, setDocumentsData] = useState<any>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sameAsCurrentAddress, setSameAsCurrentAddress] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);

  // Watch affidavitAccepted to trigger re-renders when checkbox changes
  const affidavitAccepted = Form.useWatch("affidavitAccepted", form);

  const handleFieldChange = async (
    field: string,
    value: any,
    answerType: number = eAnswerType.TEXT,
    bulkPayload?: any[],
    isSubmitted?: boolean,
  ) => {
    if (formId?.length) {
      try {
        setIsSubmitting(true);

        const answers: any[] = [];

        // Handle single field
        if (field.length > 0) {
          const trimmedValue = typeof value === "string" ? value.trim() : value;
          if (trimmedValue === null || trimmedValue === undefined) {
            form.setFieldValue(field, "");
            // Continue execution to save the empty string to backend
          }

          // Auto-detect answer type from JSON strings
          let detectedType = answerType;
          if (typeof trimmedValue === "string") {
            if (trimmedValue.startsWith("[")) {
              detectedType = eAnswerType.ARRAY;
            } else if (trimmedValue.startsWith("{")) {
              detectedType = eAnswerType.OBJECT;
            }
          }

          // Format answer based on type
          let formattedAnswer = trimmedValue;
          if (
            detectedType === eAnswerType.ARRAY ||
            detectedType === eAnswerType.OBJECT
          ) {
            formattedAnswer =
              typeof trimmedValue === "string"
                ? trimmedValue
                : JSON.stringify(trimmedValue);
          } else if (
            detectedType === eAnswerType.DATE &&
            typeof trimmedValue === "object" &&
            trimmedValue?.format
          ) {
            formattedAnswer = trimmedValue.format("YYYY-MM-DD");
          }

          answers.push({
            questionKey: field,
            answerType: detectedType,
            answer: formattedAnswer,
            part: eSTAGE.STAGE1,
          });
        }

        // Handle bulk payload (multiple answers)
        if (bulkPayload && Array.isArray(bulkPayload)) {
          answers.push(...bulkPayload);
        }

        const payload: any = {
          data: {
            id: formId,
            answers: answers,
          },
        };

        if (isSubmitted) {
          payload.data.isSubmitted = true;
        }

        const response = await post(updateField, payload);
        const { dataResponse } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode == eResultCode.SUCCESS) {
          if (isSubmitted) {
            onShowToast({
              type: ToastType.success,
              title: <FaCheck />,
              position: ToastOpen.leftBottom,
              content: "Form Submitted Successfully!",
            });
            setTimeout(() => {
              navigate("/");
            }, 1000);
          }
        } else if (returnCode == eResultCode.INVALID_REQUEST) {
          onShowToast({
            type: ToastType.error,
            title: <ExclamationCircleOutlined />,
            position: ToastOpen.leftBottom,
            content: description,
          });
        }
      } catch (error) {
        console.error("Teacher Form 1 Error : ", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Initialize auto-save hook
  const { debouncedSave, handleFormSubmit, onFormValuesChange } =
    useFormAutoSave({
      formId,
      form,
      stage: eSTAGE.STAGE1,
      handleFieldChange,
      excludeFields: [
        "TEACHER_2026_PT1_Q42",
        "TEACHER_2026_PT1_Q38",
        "TEACHER_2026_PT1_Q18_E",
      ], // Exclude file upload fields
    });

  // Centralized handler for local side-effects + auto-save
  const handleLocalFormChange = (changedValues: any, allValues: any) => {
    // 1. Run Auto Save Logic from hook
    onFormValuesChange(changedValues, allValues);

    // 2. Handle Side Effects

    // Q1 Side Effect: Copy Name to Q36 and Q40
    if ("TEACHER_2026_PT1_Q1" in changedValues) {
      const value = changedValues.TEACHER_2026_PT1_Q1;
      if (value) {
        form.setFieldValue("TEACHER_2026_PT1_Q33", value);
        debouncedSave("TEACHER_2026_PT1_Q33", value);
        form.setFieldValue("TEACHER_2026_PT1_Q35", value);
        debouncedSave("TEACHER_2026_PT1_Q35", value);
      }
    }

    // Same Address Side Effect: Copy Q8 to Q9 if checkbox is checked
    if ("TEACHER_2026_PT1_Q8" in changedValues && sameAsCurrentAddress) {
      const value = changedValues.TEACHER_2026_PT1_Q8;
      form.setFieldValue("TEACHER_2026_PT1_Q9", value);
      debouncedSave("TEACHER_2026_PT1_Q9", value);
    }

    // Q17_A Side Effect: Reset Q17_B when Q17_A changes
    if ("TEACHER_2026_PT1_Q17_A" in changedValues) {
      setVedOptions(vedMapping);
      form.setFieldValue("TEACHER_2026_PT1_Q17_B", null);
      debouncedSave("TEACHER_2026_PT1_Q17_B", null);
    }

    // Q29 Total Calculation Side Effect
    const q24Fields = [
      "TEACHER_2026_PT1_Q24_MULANT",
      "TEACHER_2026_PT1_Q24_LEVEL_1",
      "TEACHER_2026_PT1_Q24_LEVEL_2",
      "TEACHER_2026_PT1_Q24_UCCHTAR",
    ];
    if (Object.keys(changedValues).some((key) => q24Fields.includes(key))) {
      const total = q24Fields
        .map((field) => parseInt(form.getFieldValue(field)) || 0)
        .reduce((acc, curr) => acc + curr, 0);
      form.setFieldValue("TEACHER_2026_PT1_Q24_TOTAL", total);
      debouncedSave("TEACHER_2026_PT1_Q24_TOTAL", total);
    }

    // Q25 Total Calculation Side Effect
    const q25Fields = [
      "TEACHER_2026_PT1_Q25_MULANT",
      "TEACHER_2026_PT1_Q25_LEVEL_1",
      "TEACHER_2026_PT1_Q25_LEVEL_2",
      "TEACHER_2026_PT1_Q25_UCCHTAR",
    ];
    if (Object.keys(changedValues).some((key) => q25Fields.includes(key))) {
      const total = q25Fields
        .map((field) => parseInt(form.getFieldValue(field)) || 0)
        .reduce((acc, curr) => acc + curr, 0);
      form.setFieldValue("TEACHER_2026_PT1_Q25_TOTAL", total);
      debouncedSave("TEACHER_2026_PT1_Q25_TOTAL", total);
    }
  };

  // Loader Context For show Loader
  const contextForLoader = useContext(LoaderContext);
  const { isLoading, setIsLoading } = contextForLoader as any;
  const inputRequiredBasicRule: Rule[] = [
    {
      required: true,
      message: Text({ tid: "REQUIRED", def: "Required" }),
    },
  ];
  const vedMapping = useVedOptions();

  const [vedOptions, setVedOptions] = useState<any>(vedMapping);

  const tableColumns: TableProps<DataType>["columns"] = [
    {
      title: Text({ tid: "EDUCATION_LEVEL", def: "EDUCATION_LEVEL" }),
      dataIndex: "label",
      key: "label",
    },
    {
      title: Text({ tid: "STUDENT_COUNT", def: "STUDENT_COUNT" }),
      dataIndex: "value",
      key: "value",
      render: (_, record) => (
        <Form.Item
          name={record.key} // Assuming 'key' is unique for each field
          rules={inputRequiredBasicRule}
        >
          <Input
            disabled={
              disabled ||
              record.key === "TEACHER_2026_PT1_Q24_TOTAL" ||
              record.key === "TEACHER_2026_PT1_Q25_TOTAL"
              || isReviewMode
            }
            onKeyDown={allowOnlyNumericInput_onKeyDown}
          />
        </Form.Item>
      ),
    },
  ];

  const TEACHER_2026_PT1_Q24: DataType[] = [
    {
      key: "TEACHER_2026_PT1_Q24_MULANT",
      label: Text({ tid: "MULANT", def: "MULANT" }),
      value: "TEACHER_2026_PT1_Q24_MULANT",
    },
    {
      key: "TEACHER_2026_PT1_Q24_LEVEL_1",
      label: Text({ tid: "LEVEL_1", def: "LEVEL_1" }),
      value: "TEACHER_2026_PT1_Q24_LEVEL_1",
    },
    {
      key: "TEACHER_2026_PT1_Q24_LEVEL_2",
      label: Text({ tid: "LEVEL_2", def: "LEVEL_2" }),
      value: "TEACHER_2026_PT1_Q24_LEVEL_2",
    },
    {
      key: "TEACHER_2026_PT1_Q24_UCCHTAR",
      label: Text({ tid: "UCCHTAR", def: "UCCHTAR" }),
      value: "TEACHER_2026_PT1_Q24_UCCHTAR",
    },
    {
      key: "TEACHER_2026_PT1_Q24_TOTAL",
      label: Text({ tid: "TOTAL", def: "TOTAL" }),
      value: "TEACHER_2026_PT1_Q24_TOTAL",
    },
  ];

  const TEACHER_2026_PT1_Q25: DataType[] = [
    {
      key: "TEACHER_2026_PT1_Q25_MULANT", // Unique key for each field
      label: Text({ tid: "MULANT", def: "MULANT" }),
      value: "TEACHER_2026_PT1_Q25_MULANT", // Placeholder or initial value
    },
    {
      key: "TEACHER_2026_PT1_Q25_LEVEL_1",
      label: Text({ tid: "LEVEL_1", def: "LEVEL_1" }),
      value: "TEACHER_2026_PT1_Q25_LEVEL_1",
    },
    {
      key: "TEACHER_2026_PT1_Q25_LEVEL_2",
      label: Text({ tid: "LEVEL_2", def: "LEVEL_2" }),
      value: "TEACHER_2026_PT1_Q25_LEVEL_2",
    },
    {
      key: "TEACHER_2026_PT1_Q25_UCCHTAR",
      label: Text({ tid: "UCCHTAR", def: "UCCHTAR" }),
      value: "TEACHER_2026_PT1_Q25_UCCHTAR",
    },
    {
      key: "TEACHER_2026_PT1_Q25_TOTAL",
      label: Text({ tid: "TOTAL", def: "TOTAL" }),
      value: "TEACHER_2026_PT1_Q25_TOTAL",
    },
  ];

  const TEACHER_2026_PT1_Q22_A1 = Text({
    tid: "TEACHER_2026_PT1_Q22_A1",
    def: "From month/year:",
  });
  const TEACHER_2026_PT1_Q22_A2 = Text({
    tid: "TEACHER_2026_PT1_Q22_A2",
    def: "To month/year:",
  });
  const TEACHER_2026_PT1_Q22_B = Text({
    tid: "TEACHER_2026_PT1_Q22_B",
    def: "Name of Ved School:",
  });
  const TEACHER_2026_PT1_Q22_C = Text({
    tid: "TEACHER_2026_PT1_Q22_C",
    def: "Place of Ved School:",
  });
  const TEACHER_2026_PT1_Q22_D = Text({
    tid: "TEACHER_2026_PT1_Q22_D",
    def: "State:",
  });

  const TEACHER_2026_PT1_Q28_A = Text({
    tid: "TEACHER_2026_PT1_Q28_A",
    def: " ",
  });
  const TEACHER_2026_PT1_Q28_B = Text({
    tid: "TEACHER_2026_PT1_Q28_B",
    def: " ",
  });
  const TEACHER_2026_PT1_Q28_C = Text({
    tid: "TEACHER_2026_PT1_Q28_C",
    def: " ",
  });
  const TEACHER_2026_PT1_Q28_D = Text({
    tid: "TEACHER_2026_PT1_Q28_D",
    def: " ",
  });

  const MULANT = Text({ tid: "MULANT", def: "MULANT" });
  const LEVEL_1 = Text({ tid: "LEVEL_1", def: "LEVEL_1" });
  const LEVEL_2 = Text({ tid: "LEVEL_2", def: "LEVEL_2" });
  const UCCHTAR = Text({ tid: "UCCHTAR", def: "UCCHTAR" });

  const REQUIRED = Text({ tid: "REQUIRED", def: "Required" });

  const DROPDOWN_PLACEHOLDER = Text({
    tid: "DROPDOWN_PLACEHOLDER",
    def: "DROPDOWN_PLACEHOLDER",
  });

  const SELECT_DATE = Text({
    tid: "SELECT_DATE",
    def: "MM-YYYY",
  });

  const REMOVE = Text({ tid: "REMOVE", def: " " });
  const ADD = Text({ tid: "ADD", def: " " });

  const [samvedShakhaOptions, setSamvedShakhaOptions] = useState<string[]>([]);
  useEffect(() => {
    setVedOptions(vedMapping);
  }, [userLanguage]);

  useEffect(() => {
    setSamvedShakhaOptions(() => {
      return Object.values(vedMapping).flatMap((item) => item.vedashakhas);
    });
  }, []);

  /**
   * Handle "Next" button click - validates form and enters review mode
   */
  const handleNextClick = async () => {
    try {
      await form.validateFields();
      setIsReviewMode(true);
      // Scroll to top so user can review from beginning
      document
        .getElementById("teacherFormPart1")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (errorInfo: any) {
      if (errorInfo?.errorFields?.length > 0) {
        form.scrollToField(errorInfo.errorFields[0].name, {
          behavior: "smooth",
        });
      }
      console.log("Validation failed:", errorInfo);
    }
  };

  /**
   * Handle "Back" button click - exits review mode to allow editing
   */
  const handleBackClick = () => {
    setIsReviewMode(false);
  };

  /**
   * Handle "Same as Current Address" checkbox change
   */
  const handleSameAddressChange = useCallback(
    (checked: boolean) => {
      setSameAsCurrentAddress(checked);
      if (checked) {
        const correspondenceAddress = form.getFieldValue("TEACHER_2026_PT1_Q8");
        form.setFieldValue("TEACHER_2026_PT1_Q9", correspondenceAddress);
        handleFieldChange("TEACHER_2026_PT1_Q9", correspondenceAddress || "");
      }
    },
    [form, handleFieldChange],
  );

  const getMappedData = (data: any) => {
    if (!data) return {};

    const mapArrayData = (
      sourceArr: any[],
      prefixFrom: string,
      prefixTo: string,
      keys: string[],
    ) => {
     
      if (!Array.isArray(sourceArr) || sourceArr.length === 0 || !sourceArr) {
        
        const emptyItem: any = {};
        keys.forEach((k) => {
          emptyItem[`${prefixTo}${k}`] = "";
        });
        return [emptyItem];
      }
      return sourceArr.map((item) => {
        const newItem: any = {};
        keys.forEach((k) => {
          newItem[`${prefixTo}${k}`] = item[`${prefixFrom}${k}`] || "";
        });
        return newItem;
      });
    };

    const mappedData: { [key: string]: any } = {
      // Name - Q1
      TEACHER_2026_PT1_Q1: data.TEACHER_PT1_Q1 || "",

      // Father's Name - Q2 (2025 Q5)
      TEACHER_2026_PT1_Q2: data.TEACHER_PT1_Q5 || "",

      // Mother's Name - Q3 (2025 Q6)
      TEACHER_2026_PT1_Q3: data.TEACHER_PT1_Q6 || "",

      // DOB - Q4 (2025 Q3)
      TEACHER_2026_PT1_Q4: Utils.parseToDayjs(data.TEACHER_PT1_Q3),

      // Birth Place - Q5 (2025 Q4)
      TEACHER_2026_PT1_Q5: data.TEACHER_PT1_Q4 || "",

      // Rishi-Gotra - Q6 (2025 Q7)
      TEACHER_2026_PT1_Q6: data.TEACHER_PT1_Q7 || "",

      // Aadhaar - Q7 (2025 Q2)
      TEACHER_2026_PT1_Q7: data.TEACHER_PT1_Q2 || "",

      // Address (Correspondence) - Q8 (2025 Q9)
      TEACHER_2026_PT1_Q8: data.TEACHER_PT1_Q9 || "",

      // Address (Permanent) - Q9 (New in 2026)
      TEACHER_2026_PT1_Q9: data.TEACHER_PT1_Q9 || "",

      // Mobile - Q10 (2025 Q10)
      TEACHER_2026_PT1_Q10: data.TEACHER_PT1_Q10 || "",

      // Email - Q11 (2025 Q11)
      TEACHER_2026_PT1_Q11: data.TEACHER_PT1_Q11 || "",

      // Language - Q12 (2025 Q12)
      TEACHER_2026_PT1_Q12: data.TEACHER_PT1_Q12 || "",

      // Married - Q13 (2025 Q13)
      TEACHER_2026_PT1_Q13: data.TEACHER_PT1_Q13 || "",

      // Wife Name - Q14 (2025 Q14)
      TEACHER_2026_PT1_Q14: data.TEACHER_PT1_Q14 || "",

      // Student Count - Q15 (2025 Q15)
      TEACHER_2026_PT1_Q15: data.TEACHER_PT1_Q15 || "",

      // Swa-ved Shakha - Q16 (2025 Q8)
      TEACHER_2026_PT1_Q16: data.TEACHER_PT1_Q8 || "",

      // Adhit Ved - Q17_A (2025 Q16)
      TEACHER_2026_PT1_Q17_A: data.TEACHER_PT1_Q16 || "",

      // Adhit Vedshakha - Q17_B (2025 Q17)
      TEACHER_2026_PT1_Q17_B: data.TEACHER_PT1_Q17 || "",

      // Exam Name - Q18_A (2025 Q19)
      TEACHER_2026_PT1_Q18_A: data.TEACHER_PT1_Q19 || "",

      // Exam Year - Q18_B (2025 Q18)
      TEACHER_2026_PT1_Q18_B: data.TEACHER_PT1_Q18
        ? Utils.parseToDayjs(data.TEACHER_PT1_Q18)
        : null,

      // Exam Category - Q18_C (2025 Q18_EXAM_CATEGORY)
      TEACHER_2026_PT1_Q18_C: data.TEACHER_PT1_Q18_EXAM_CATEGORY || "",

      // Exam Institute - Q18_D (2025 Q18_EXAM_INSTITUTE)
      TEACHER_2026_PT1_Q18_D: data.TEACHER_PT1_Q18_EXAM_INSTITUTE || "",

      // Guru Name - Q19 (2025 Q21)
      TEACHER_2026_PT1_Q19: data.TEACHER_PT1_Q21 || "",

      // Guru Relative - Q20 (2025 Q22)
      TEACHER_2026_PT1_Q20_A: data.TEACHER_PT1_Q22 ? "YES" : "NO",
      TEACHER_2026_PT1_Q20_B: data.TEACHER_PT1_Q22 || "",

      // Extinct Veda - Q21 (2025 Q23)
      TEACHER_2026_PT1_Q21_A: data.TEACHER_PT1_Q23 ? "YES" : "NO",
      TEACHER_2026_PT1_Q21_B: data.TEACHER_PT1_Q23 || "",

      // Teaching History - Q22 (2025 Q24 Array)
      TEACHER_2026_PT1_Q22: mapArrayData(
        data.TEACHER_PT1_Q24,
        "TEACHER_PT1_Q24_",
        "TEACHER_2026_PT1_Q22_",
        ["A1", "A2", "B", "C", "D"],
      ),

      // Institution Details - Q23 (2025 Q25, Q26, Q27)
      TEACHER_2026_PT1_Q23: data.TEACHER_PT1_Q25 || "",
      TEACHER_2026_PT1_Q23_A: data.TEACHER_PT1_Q26 || "",
      TEACHER_2026_PT1_Q23_B: data.TEACHER_PT1_Q27 || "",

      // Q24 Table Fields (2025 Q28)
      TEACHER_2026_PT1_Q24_MULANT: data.TEACHER_PT1_Q28_MULANT || "",
      TEACHER_2026_PT1_Q24_LEVEL_1: data.TEACHER_PT1_Q28_LEVEL_1 || "",
      TEACHER_2026_PT1_Q24_LEVEL_2: data.TEACHER_PT1_Q28_LEVEL_2 || "",
      TEACHER_2026_PT1_Q24_UCCHTAR: data.TEACHER_PT1_Q28_UCCHTAR || "",
      TEACHER_2026_PT1_Q24_TOTAL: data.TEACHER_PT1_Q28_TOTAL || 
     parseInt(data.TEACHER_2026_PT1_Q24_MULANT) + parseInt(data.TEACHER_2026_PT1_Q24_LEVEL_1) + parseInt(data.TEACHER_2026_PT1_Q24_LEVEL_2) + parseInt(data.TEACHER_2026_PT1_Q24_UCCHTAR),
      // TEACHER_2026_PT1_Q24_TOTAL: Calculated automatically

      // Q25 Table Fields (2025 Q29)
      TEACHER_2026_PT1_Q25_MULANT: data.TEACHER_PT1_Q29_MULANT || "",
      TEACHER_2026_PT1_Q25_LEVEL_1: data.TEACHER_PT1_Q29_LEVEL_1 || "",
      TEACHER_2026_PT1_Q25_LEVEL_2: data.TEACHER_PT1_Q29_LEVEL_2 || "",
      TEACHER_2026_PT1_Q25_UCCHTAR: data.TEACHER_PT1_Q29_UCCHTAR || "",
      // TEACHER_2026_PT1_Q25_TOTAL: Calculated automatically

      // Efforts - Q27 (2025 Q30)
      TEACHER_2026_PT1_Q27: data.TEACHER_PT1_Q30 || "",

      // Relatives Studying - Q28 (2025 Q31 Array)
      TEACHER_2026_PT1_Q28_YES_NO: data.TEACHER_PT1_Q31 ? "YES" : "NO",
      TEACHER_2026_PT1_Q28: mapArrayData(
        data.TEACHER_PT1_Q31,
        "TEACHER_PT1_Q31_",
        "TEACHER_2026_PT1_Q28_",
        ["A", "B", "C", "D"],
      ),

      // Efforts Propagation - Q29 (2025 Q32)
      TEACHER_2026_PT1_Q29: data.TEACHER_PT1_Q32 || "",

      // Achievements - Q30 (2025 Q33)
      TEACHER_2026_PT1_Q30: data.TEACHER_PT1_Q33 || "",

      // Ideal Teacher Qualities - Q31 (2025 Q34)
      TEACHER_2026_PT1_Q31: data.TEACHER_PT1_Q34 || "",

      // Explain Importance - Q32 (2025 Q35)
      TEACHER_2026_PT1_Q32: data.TEACHER_PT1_Q35 || "",

      // Affidavit Name - Q33 (2025 Q36)
      TEACHER_2026_PT1_Q33: data.TEACHER_PT1_Q36 || "",

      // Affidavit Years - Q34 (2025 Q37)
      TEACHER_2026_PT1_Q34: data.TEACHER_PT1_Q37 || "",

      // Other Affidavit Fields (2025 Q43, Q44, Q45) -> 2026 Q35, Q36, Q37
      TEACHER_2026_PT1_Q35: data.TEACHER_PT1_Q43 || "",
      TEACHER_2026_PT1_Q36: data.TEACHER_PT1_Q44 || "",
      TEACHER_2026_PT1_Q37: data.TEACHER_PT1_Q45
        ? Utils.parseToDayjs(data.TEACHER_PT1_Q45)
        : null,
    };

    // Filter out null/undefined values
    return Object.fromEntries(
      Object.entries(mappedData).filter(
        ([_, value]) => value !== null && value !== undefined && value !== "",
      ),
    );
  };

  const getPreviousFormData = async () => {
    setIsLoading(true);
    try {
      const payload: any = {
        data: {
          part: eSTAGE.STAGE1,
          year: CURRENT_YEAR - 1,
          userFormType: eUSER_FORM_TYPE.TEACHER,
          userId: userId,
          formFor: formFor,
        },
      };

      const response = await post(GetFormData, payload);
      const { dataResponse, data } = response;
      const { returnCode } = dataResponse;

      if (returnCode == eResultCode.SUCCESS) {
        const formData = Object.values(data?.teacherPt1Forms || {}).every(
          (value) => value === null,
        )
          ? {}
          : data?.teacherPt1Forms || {};

        // List of fields to exclude (file input fields)
        const jsonStringFields = [
          "TEACHER_2026_PT1_Q22",
          "TEACHER_2026_PT1_Q28",
        ];
        const fileFields = ["TEACHER_2026_PT1_Q18_E", "TEACHER_2026_PT1_Q38"];
        const yearFields = ["TEACHER_2026_PT1_Q18_B", "TEACHER_2026_PT1_Q37"];

        const formattedData = Object.keys(formData).reduce(
          (acc: { [key: string]: any }, key) => {
            if (jsonStringFields.includes(key)) {
              try {
                const parsedArr = formData[key]
                  ? JSON.parse(formData[key])
                  : [{}];
                acc[key] = parsedArr;
              } catch (error) {
                console.error(error);
              }
            } else if (fileFields.includes(key)) {
              acc[key] = "";
            } else if (yearFields.includes(key)) {
              acc[key] = Utils.parseToDayjs(formData[key]);
            } else {
              acc[key] = formData[key];
            }
            return acc;
          },
          {},
        );

        const mappedData = getMappedData(formattedData);

        // Use mapped data (with 2026 field names) instead of original formattedData
        if (mappedData && Object.keys(mappedData).length > 0) {
          form.setFieldsValue(mappedData);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getFormData = async () => {
    setIsLoading(true);
    try {
      const payload: any = {
        data: {
          id: formId,
          part: eSTAGE.STAGE1,
          year: CURRENT_YEAR,
          userFormType: eUSER_FORM_TYPE.TEACHER,
          userId: userId,
          formFor: formFor,
        },
      };

      const response = await post(GetFormData, payload);
      const { dataResponse, data } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode == eResultCode.SUCCESS && Array.isArray(data)) {
        // Convert array of answers to form field values
        const formattedData: { [key: string]: any } = {};
        const docs: any[] = [];

        data.forEach((answer: any) => {
          const { questionKey, answerType, answer: answerValue } = answer;

          if (!questionKey) return;

          // Parse based on answerType
          switch (answerType) {
            case eAnswerType.ARRAY:
            case eAnswerType.OBJECT:
              try {
                let parsedValue = answerValue
                  ? JSON.parse(answerValue)
                  : answerType === eAnswerType.ARRAY
                    ? []
                    : {};

                // Handle date conversion for Teaching History array (Q22)
                if (
                  questionKey === "TEACHER_2026_PT1_Q22" &&
                  Array.isArray(parsedValue)
                  && parsedValue.length > 0 && parsedValue[0] !== null
                ) {
                  parsedValue = parsedValue.map((item: any) => ({
                    ...item,
                    TEACHER_2026_PT1_Q22_A1: item.TEACHER_2026_PT1_Q22_A1
                      ? Utils.parseToDayjs(item.TEACHER_2026_PT1_Q22_A1)
                      : null,
                    TEACHER_2026_PT1_Q22_A2: item.TEACHER_2026_PT1_Q22_A2
                      ? Utils.parseToDayjs(item.TEACHER_2026_PT1_Q22_A2)
                      : null,
                  }));
                }

                formattedData[questionKey] = parsedValue;
              } catch (error) {
                console.error(`Error parsing ${questionKey}:`, error);
                formattedData[questionKey] =
                  answerType === eAnswerType.ARRAY ? [] : {};
              }
              break;
            case eAnswerType.FILE:
              // File fields store document ID, don't set in form
              formattedData[questionKey] = answerValue || "";
              if (answerValue) {
                docs.push({
                  docPath: answerValue,
                  labelName: questionKey, // linking doc to question
                  docName: "Document", // Default name
                });
              }
              break;
            case eAnswerType.DATE:
              // Convert date string to dayjs object for DatePicker
              formattedData[questionKey] = Utils.parseToDayjs(answerValue);
              break;
            case eAnswerType.TEXT:
            default:
              formattedData[questionKey] = answerValue || "";

              // Force Date Conversion for known date fields if they come as TEXT
              if (DATE_FIELD_KEYS.includes(questionKey) && answerValue) {
                formattedData[questionKey] = Utils.parseToDayjs(answerValue);
              }
              break;
          }
        });

        if (docs.length > 0) {
          setDocumentsData(docs);
        }

        if (Object.keys(formattedData).length > 0) {
          form.setFieldsValue(formattedData);

          // Sync affidavitAccepted state with loaded form data
          if (
            formattedData.affidavitAccepted ||
            formattedData.affidavitAccepted === "1"
          ) {
            form.setFieldValue("affidavitAccepted", true);
          }
        }
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (formId && formFor && user) {
      const fetchData = async () => {
        await getPreviousFormData();
        await getFormData();

        let specificUserData: any = null;
        if (userId && Number(formFor) === eFORM_FOR.OTHER) {
          try {
            const userResponse = await post(GetSpecificUserData, {
              data: { id: userId },
            });
            if (
              userResponse?.dataResponse?.returnCode === eResultCode.SUCCESS &&
              userResponse?.data
            ) {
              specificUserData = userResponse.data;
            }
          } catch (error) {
            console.error("Error fetching specific user data:", error);
          }
        }

        const userDataToUse = specificUserData || user;

        // Auto-fill logic: Run AFTER fetching data to avoid overwriting existing user input.
        // Only fill fields that are currently empty/null in the form.
        const autoFillData: { [key: string]: any } = {
          TEACHER_2026_PT1_Q1: userDataToUse?.fullName,
          TEACHER_2026_PT1_Q33: userDataToUse?.fullName,
          TEACHER_2026_PT1_Q35: userDataToUse?.fullName,
          TEACHER_2026_PT1_Q7: userDataToUse?.aadhaarCardNumber,
          TEACHER_2026_PT1_Q4: userDataToUse?.dateOfBirth
            ? dayjs(userDataToUse.dateOfBirth, "YYYY-MM-DD")
            : null,
          TEACHER_2026_PT1_Q37: dayjs(),
          TEACHER_2026_PT1_Q2: userDataToUse?.fatherName,
          TEACHER_2026_PT1_Q10: userDataToUse?.mobileNumber,
          TEACHER_2026_PT1_Q11: userDataToUse?.emailId,
        };

        const fieldsToSet: { [key: string]: any } = {};
        const bulkPayload: any[] = [];

        Object.entries(autoFillData).forEach(([key, value]) => {
          // Check if field is currently empty in the form
          const currentValue = form.getFieldValue(key);
          if (
            !currentValue &&
            value !== undefined &&
            value !== null &&
            value !== ""
          ) {
            // Set value for Form State
            fieldsToSet[key] = value;

            // Prepare value for Backend Save (Convert Dates to String)
            let answerStr = String(value);
            if (dayjs.isDayjs(value)) {
              answerStr = value.isValid() ? value.format("YYYY-MM-DD") : "";
            }

            if (answerStr) {
              bulkPayload.push({
                questionKey: key,
                answerType: eAnswerType.TEXT,
                answer: answerStr,
                part: eSTAGE.STAGE1,
              });
            }
          }
        });

        if (bulkPayload.length > 0) {
          form.setFieldsValue(fieldsToSet);
          handleFieldChange("", "", eAnswerType.TEXT, bulkPayload);
        }
      };

      fetchData();
    }
  }, [formId, formFor, userId, user]);

  useEffect(() => {
    if (searchParams) {
      // Decrypt and set formId and formFor from URL search parameters
      const formId = EncryptUtils.decrypt(searchParams.get("id") || "");
      const formFor = EncryptUtils.decrypt(searchParams.get("ff") || "");
      const uId = EncryptUtils.decrypt(searchParams.get("ui") || "");
      setFormId(formId);
      setFormFor(formFor);
      setUserId(uId);
    }
    if (currentFormId) {
      // Decrypt and set formId from props if available
      setFormId(EncryptUtils.decrypt(currentFormId));
    }
    if (currentUserId) {
      setUserId(EncryptUtils.decrypt(currentUserId));
    }
    if (currentFormFor) {
      setFormFor(EncryptUtils.decrypt(currentFormFor));
    }
  }, [searchParams, currentFormId, currentUserId, currentFormFor]);

  useEffect(() => {
    if (isRelativeTaught === eYES_OR_NO.YES) {
      const currentList = form.getFieldValue("TEACHER_2026_PT1_Q28");
      if (!currentList || currentList.length === 0) {
        form.setFieldValue("TEACHER_2026_PT1_Q28", [{}]);
      }
    }
  }, [isRelativeTaught]);

  const Q22_B_PLACEHOLDER = Text({
    tid: "TEACHER_2026_PT1_Q22_B_PLACEHOLDER",
    def: "Ex. Sanatan Vaidik Shikshan Sansthan",
  });
  const Q22_C_PLACEHOLDER = Text({
    tid: "TEACHER_2026_PT1_Q22_C_PLACEHOLDER",
    def: "Ex. Kasar, Charbhuja, Rajsamand",
  });
  const Q22_D_PLACEHOLDER = Text({
    tid: "TEACHER_2026_PT1_Q22_D_PLACEHOLDER",
    def: "Ex. Rajasthan",
  });
  const Q28_A_PLACEHOLDER = Text({
    tid: "TEACHER_2026_PT1_Q28_A_PLACEHOLDER",
    def: "Ex. Mohan Sharma",
  });
  const Q28_B_PLACEHOLDER = Text({
    tid: "TEACHER_2026_PT1_Q28_B_PLACEHOLDER",
    def: "Ex. Son",
  });
  const Q28_D_PLACEHOLDER = Text({
    tid: "TEACHER_2026_PT1_Q28_D_PLACEHOLDER",
    def: "Ex. Veda Teacher",
  });
  const Q35_PLACEHOLDER = Text({
    tid: "TEACHER_2026_PT1_FULL_NAME_PLACEHOLDER",
    def: "Ex. Rajesh Kumar Sharma",
  });
  const Q36_PLACEHOLDER = Text({
    tid: "TEACHER_2026_PT1_PLACE_PLACEHOLDER",
    def: "Ex. New Delhi",
  });
  const Q21_B_PLACEHOLDER = Text({
    tid: "TEACHER_2026_PT1_Q21_B_PLACEHOLDER",
    def: "Ex. Atharva Veda",
  });

  return (
    <div
      id='teacherFormPart1'
      className={`${styles.formWrapper} studentPartTwo studentTeacherSchoolFormWrapper`}
    >
      <Form
        {...formItemLayout}
        form={form}
        name='register'
        onFinish={handleFormSubmit}
        onValuesChange={handleLocalFormChange}
        scrollToFirstError
        layout='vertical'
      >
        <Row>
          <Flex
            gap='middle'
            align='start'
            justify='space-between'
            style={{ width: "100%" }}
          >
            <Col>
              <Typography.Title level={3}>
                {Text({
                  tid: "TEACHER_2026_PART_1_FORM_HEADING",
                  def: "TEACHER_PART_1_FORM_HEADING",
                })}
              </Typography.Title>
              {!disabled && (
                <Typography.Text type='danger'>
                  {Text({
                    tid: "all_fields_are_mandatory",
                    def: "All fields are mandatory!",
                  })}
                </Typography.Text>
              )}
            </Col>
          </Flex>
        </Row>
          {isReviewMode && (
                    <Row style={{ marginBottom: 10 }}>
                      <Typography.Text type='warning'>
                        <CheckCircleOutlined style={{ marginRight: 6 }} />
                        {Text({
                          tid: "REVIEW_MODE_INSTRUCTION",
                          def: "You are in review mode. You can only view the submitted data. To make changes, please switch to edit mode.",
                        })}
                      </Typography.Text>
                    </Row>
                  )}
        {/* 1. Name */}
        <Row>
          <Col
            sm={24}
            lg={12}
            xl={8}
            style={{ width: "100%" }}
          >
            <Form.Item
              name='TEACHER_2026_PT1_Q1'
              label={Text({
                tid: "TEACHER_2026_PT1_Q1",
                def: "Name",
              })}
              rules={inputRequiredBasicRule}
            >
              <Input
                disabled={disabled || isReviewMode}
                placeholder={Text({
                  tid: "TEACHER_2026_PT1_Q1_PLACEHOLDER",
                  def: "Ex. Shridhar Sharma",
                })}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 2. Father's name */}
        <Row>
          <Col
            sm={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='TEACHER_2026_PT1_Q2'
              label={Text({
                tid: "TEACHER_2026_PT1_Q2",
                def: "Father's name",
              })}
              rules={inputRequiredBasicRule}
            >
              <Input
                disabled={disabled || isReviewMode}
                placeholder={Text({
                  tid: "TEACHER_2026_PT1_Q2_PLACEHOLDER",
                  def: "Ex. Lakshmidhar Sharma",
                })}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 3. Mother's name */}
        <Row>
          <Col
            sm={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='TEACHER_2026_PT1_Q3'
              label={Text({
                tid: "TEACHER_2026_PT1_Q3",
                def: "Mother's name",
              })}
              rules={inputRequiredBasicRule}
            >
              <Input
                disabled={disabled || isReviewMode}
                placeholder={Text({
                  tid: "TEACHER_2026_PT1_Q3_PLACEHOLDER",
                  def: "Ex. Vimala Sharma",
                })}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 4. DOB */}
        <Row>
          <Col
            sm={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='TEACHER_2026_PT1_Q4'
              label={Text({
                tid: "TEACHER_2026_PT1_Q4",
                def: "Date of birth",
              })}
              rules={inputRequiredBasicRule}
            >
              <DatePicker
                format='DD-MM-YYYY'
                placeholder={Text({
                  tid: "TEACHER_2026_PT1_Q4_PLACEHOLDER",
                  def: "01-01-1981",
                })}
                disabled={disabled || isReviewMode}
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 5. Birth Place */}
        <Row>
          <Col
            sm={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='TEACHER_2026_PT1_Q5'
              label={Text({
                tid: "TEACHER_2026_PT1_Q5",
                def: "Birth Place",
              })}
              rules={inputRequiredBasicRule}
            >
              <Input
                disabled={disabled || isReviewMode}
                placeholder={Text({
                  tid: "TEACHER_2026_PT1_Q5_PLACEHOLDER",
                  def: "Ex. Udaipur",
                })}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 6. Rishi-Gotra */}
        <Row>
          <Col
            sm={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='TEACHER_2026_PT1_Q6'
              label={Text({
                tid: "TEACHER_2026_PT1_Q6",
                def: "Rishi-Gotra",
              })}
              rules={inputRequiredBasicRule}
            >
              <Input
                disabled={disabled || isReviewMode}
                placeholder={Text({
                  tid: "TEACHER_2026_PT1_Q6_PLACEHOLDER",
                  def: "Ex. Bharadwaj",
                })}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 7. Aadhaar Card Number */}
        <Row>
          <Col
            sm={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='TEACHER_2026_PT1_Q7'
              label={Text({
                tid: "TEACHER_2026_PT1_Q7",
                def: "Aadhaar No.",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
                {
                  pattern: /^[2-9]{1}[0-9]{11}$/, // Aadhaar must be 12 digits and should not start with 0 or 1
                  message: Text({
                    tid: "aadhaar_card_validation",
                    def: "Aadhaar must be 12 digits and should not start with 0 or 1",
                  }),
                },
              ]}
            >
              <Input
                maxLength={12}
                onKeyDown={allowOnlyNumericInput_onKeyDown}
                disabled={disabled || isReviewMode}
                placeholder={Text({
                  tid: "TEACHER_2026_PT1_Q7_PLACEHOLDER",
                  def: "1234 1234 1234",
                })}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 8. Address for correspondence with PIN code */}
        <Row>
          <Col
            sm={24}
            lg={12}
            xl={12}
          >
            <Form.Item
              name='TEACHER_2026_PT1_Q8'
              label={Text({
                tid: "TEACHER_2026_PT1_Q8",
                def: "Address for correspondence with PIN code",
              })}
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  disabled={disabled || isReviewMode}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                  style={{ paddingBottom: 20 }}
                  placeholder={Text({
                    tid: "TEACHER_2026_PT1_Q8_PLACEHOLDER",
                    def: "Ex. House no. 51, Hanuman Ghat, Varanasi, Uttar Pradesh, PIN-221001",
                  })}
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='TEACHER_2026_PT1_Q8'
            />
          </Col>
        </Row>
        {/* Same as Current Address Checkbox */}
        {!disabled && (
          <Row
            style={{
              marginBottom: 15,
            }}
          >
            <Col
              xs={24}
              lg={12}
              xl={12}
            >
              <Checkbox
                checked={sameAsCurrentAddress}
                onChange={(e) => handleSameAddressChange(e.target.checked)}
                disabled={isReviewMode}
              >
                {Text({
                  tid: "SAME_AS_CORRESPONDENCE_ADDRESS",
                  def: "Same as correspondence address",
                })}
              </Checkbox>
            </Col>
          </Row>
        )}

        {/* 9. Permanent address with PIN code */}
        <Row>
          <Col
            sm={24}
            lg={12}
            xl={12}
          >
            <Form.Item
              name='TEACHER_2026_PT1_Q9'
              label={Text({
                tid: "TEACHER_2026_PT1_Q9",
                def: "Permanent address with PIN code",
              })}
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  disabled={disabled || isReviewMode}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                  style={{ paddingBottom: 20 }}
                  placeholder={Text({
                    tid: "TEACHER_2026_PT1_Q9_PLACEHOLDER",
                    def: "Ex. House no. 51, Hanuman Ghat, Varanasi, Uttar Pradesh, PIN-221001",
                  })}
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='TEACHER_2026_PT1_Q9'
            />
          </Col>
        </Row>


        {/* 10. Your mobile number */}
        <Row>
          <Col
            sm={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='TEACHER_2026_PT1_Q10'
              label={Text({
                tid: "TEACHER_2026_PT1_Q10",
                def: "Your mobile number",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
                {
                  pattern: Regex.MOBILENO, // Updated pattern
                  message: Text({
                    tid: "mobile_number_validation",
                    def: "Enter a valid 10-digit mobile number",
                  }),
                },
              ]}
            >
              <Input
                maxLength={10}
                disabled={disabled || isReviewMode}
                onKeyDown={allowOnlyNumericInput_onKeyDown}
                placeholder={Text({
                  tid: "TEACHER_2026_PT1_Q10_PLACEHOLDER",
                  def: "Ex. 7357658777",
                })}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 11. Your email ID */}
        <Row>
          <Col
            sm={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='TEACHER_2026_PT1_Q11'
              label={Text({
                tid: "TEACHER_2026_PT1_Q11",
                def: "Your email ID",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
                {
                  pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: Text({
                    tid: "email_validation",
                    def: "Enter a valid email address",
                  }),
                },
              ]}
            >
              <Input
                type='email'
                disabled={disabled || isReviewMode}
                placeholder={Text({
                  tid: "TEACHER_2026_PT1_Q11_PLACEHOLDER",
                  def: "Ex. applications@bharatatmapuraskar.org",
                })}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 12. In which language should the relevant conversation be conducted with you? */}
        <Row>
          <Col
            xs={24}
            lg={24}
            xl={24}
          >
            <Form.Item
              name='TEACHER_2026_PT1_Q12'
              label={Text({
                tid: "TEACHER_2026_PT1_Q12",
                def: "In which language should the relevant conversation be conducted with you?",
              })}
              rules={inputRequiredBasicRule}
            >
              <Radio.Group disabled={disabled || isReviewMode}>
                <Radio value='English'>
                  {Text({ tid: "ENGLISH", def: "English" })}
                </Radio>
                <Radio value='हिन्दी'>
                  {Text({ tid: "HINDI", def: "Hindi" })}
                </Radio>
                <Radio value='संस्कृतम्'>
                  {Text({ tid: "SANSKRIT", def: "Sanskrit" })}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
        {/* 13. Are you married? */}
        <Row>
          <Col
            xs={24}
            lg={24}
            xl={24}
          >
            <Form.Item
              name='TEACHER_2026_PT1_Q13'
              label={Text({
                tid: "TEACHER_2026_PT1_Q13",
                def: "Are you married?",
              })}
              rules={inputRequiredBasicRule}
            >
              <Radio.Group disabled={disabled || isReviewMode}>
                <Radio value='YES'>
                  {Text({ tid: "MARRIED", def: "Married" })}
                </Radio>
                <Radio value='NO'>
                  {Text({ tid: "UNMARRIED", def: "Unmarried" })}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
        {/* 14. wife's Name */}
        {isMarried === eYES_OR_NO.YES && (
          <Row>
            <Col
              sm={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='TEACHER_2026_PT1_Q14'
                label={Text({
                  tid: "TEACHER_2026_PT1_Q14",
                  def: "Wife's name",
                })}
                rules={inputRequiredBasicRule}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "TEACHER_2026_PT1_Q14_PLACEHOLDER",
                    def: "Ex. Lilavati Sharma",
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
        {/* 15. Special for Kumaradhyapak: If you are a Kumaradhyapak or you are imparting Ved education in your home, then what is the number of Ved students in your Ved Vidyalaya? */}
        <Row>
          <Col sm={24}>
            <Form.Item
              name='TEACHER_2026_PT1_Q15'
              label={Text({
                tid: "TEACHER_2026_PT1_Q15",
                def: "Special for Kumaradhyapak: If you are a Kumaradhyapak or you are imparting Ved education in your home, then what is the number of Ved students in your Ved Vidyalaya?",
              })}
              rules={inputRequiredBasicRule}
            >
              <Input
                disabled={disabled || isReviewMode}
                maxLength={10}
                onKeyDown={allowOnlyNumericInput_onKeyDown}
                placeholder={Text({
                  tid: "TEACHER_2026_PT1_Q15_PLACEHOLDER",
                  def: "15",
                })}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 16. Swa-ved Shakha : */}
        <Row>
          <Col
            sm={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='TEACHER_2026_PT1_Q16'
              label={Text({
                tid: "TEACHER_2026_PT1_Q16",
                def: "Swa-ved Shakha",
              })}
              rules={inputRequiredBasicRule}
            >
              <Select
                disabled={disabled || isReviewMode}
                placeholder={DROPDOWN_PLACEHOLDER}
              >
                {samvedShakhaOptions &&
                  samvedShakhaOptions.map((item: string) => (
                    <Select.Option
                      key={item}
                      value={item}
                    >
                      {item}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        {/* 17_A. ADHEET VED: */}
        <Row>
          <Col
            sm={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='TEACHER_2026_PT1_Q17_A'
              label={Text({
                tid: "TEACHER_2026_PT1_Q17_A",
                def: "Adhit Ved :",
              })}
              rules={inputRequiredBasicRule}
            >
              <Select
                disabled={disabled || isReviewMode}
                placeholder={DROPDOWN_PLACEHOLDER}
              >
                {vedOptions &&
                  Object.keys(vedOptions).map((ved) => (
                    <Select.Option
                      key={ved}
                      value={vedOptions[ved].label}
                    >
                      {vedOptions[ved].label}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        {/* 17_B Adhit Vedshakha : */}
        <Row>
          <Col
            sm={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='TEACHER_2026_PT1_Q17_B'
              label={Text({
                tid: "TEACHER_2026_PT1_Q17_B",
                def: "Adhit Vedshakha :",
              })}
              rules={inputRequiredBasicRule}
            >
              <Select
                disabled={disabled || isReviewMode}
                placeholder={DROPDOWN_PLACEHOLDER}
              >
                {vedOptions &&
                  (() => {
                    const selectedVedLabel = form.getFieldValue(
                      "TEACHER_2026_PT1_Q17_A",
                    );
                    // Find the ved entry that matches the selected label
                    const vedEntry: any = Object.values(vedOptions).find(
                      (ved: any) => ved.label === selectedVedLabel,
                    );
                    return vedEntry?.vedashakhas.map((vedashakha: string) => (
                      <Select.Option
                        key={vedashakha}
                        value={vedashakha}
                      >
                        {vedashakha}
                      </Select.Option>
                    ));
                  })()}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/*  18. Which was the highest exam you passed related to Ved knowledge? : */}
        <>
          <Row>
            <Col sm={24}>
              <Typography.Text>
                {Text({
                  tid: "TEACHER_2026_PT1_Q18_HEADING",
                  def: "Which was the highest exam you passed related to Ved knowledge?",
                })}
              </Typography.Text>
            </Col>
          </Row>
          <div className='QuestionsSubSection'>
            {/* 18_A. Name of exam */}
            <Row>
              <Col
                sm={24}
                lg={12}
                xl={8}
              >
                <Form.Item
                  name='TEACHER_2026_PT1_Q18_A'
                  label={Text({
                    tid: "TEACHER_2026_PT1_Q18_A",
                    def: "Name of exam",
                  })}
                  rules={inputRequiredBasicRule}
                >
                  <Input
                    disabled={disabled || isReviewMode}
                    placeholder={Text({
                      tid: "TEACHER_2026_PT1_Q18_A_PLACEHOLDER",
                      def: "Ex. Salakshana Ghananta",
                    })}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* 18_B. Year */}
            <Row>
              <Col
                sm={24}
                lg={12}
                xl={8}
              >
                <Form.Item
                  name='TEACHER_2026_PT1_Q18_B'
                  label={Text({
                    tid: "TEACHER_2026_PT1_Q18_B",
                    def: "Year",
                  })}
                  rules={inputRequiredBasicRule}
                >
                  <DatePicker
                    disabled={disabled || isReviewMode}
                    format='MM-YYYY'
                    onKeyDown={(e) => e.preventDefault()}
                    placeholder={Text({
                      tid: "TEACHER_2026_PT1_Q18_B_PLACEHOLDER",
                      def: "June-2011",
                    })}
                    picker='month'
                  />
                </Form.Item>
              </Col>
            </Row>

            {/*18_C exam cateGORY */}
            <Row>
              <Col
                sm={24}
                lg={12}
                xl={8}
              >
                <Form.Item
                  name='TEACHER_2026_PT1_Q18_C'
                  label={Text({
                    tid: "TEACHER_2026_PT1_Q18_C",
                    def: "Exam category",
                  })}
                  rules={inputRequiredBasicRule}
                >
                  <Select
                    disabled={disabled || isReviewMode}
                    placeholder={DROPDOWN_PLACEHOLDER}
                  >
                    <Select.Option value='FIRST'>
                      {Text({ tid: "FIRST", def: "प्रथम" })}
                    </Select.Option>
                    <Select.Option value='SECOND'>
                      {Text({ tid: "SECOND", def: "द्वितीय" })}
                    </Select.Option>
                    <Select.Option value='THIRD'>
                      {Text({ tid: "THIRD", def: "तृतीय" })}
                    </Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            {/* 18_D. exam center */}
            <Row>
              <Col
                sm={24}
                lg={12}
                xl={8}
              >
                <Form.Item
                  name='TEACHER_2026_PT1_Q18_D'
                  label={Text({
                    tid: "TEACHER_2026_PT1_Q18_D",
                    def: "Exam intitute",
                  })}
                  rules={inputRequiredBasicRule}
                >
                  <Select
                    disabled={disabled || isReviewMode}
                    placeholder={DROPDOWN_PLACEHOLDER}
                    onChange={(value) =>
                      handleFieldChange("TEACHER_2026_PT1_Q18_D", value)
                    }
                  >
                    {EXAM_INSTITUTE_LIST.map((item) => (
                      <Select.Option
                        key={item.value}
                        value={item.value}
                      >
                        {Text({
                          tid: item.value,
                          def: item.label,
                        })}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              {isInstituteOther === "INSTITUTE_OTHER" && (
                <Col
                  sm={24}
                  lg={12}
                  xl={8}
                >
                  <Form.Item
                    name='TEACHER_2026_PT1_Q18_D_OTHER'
                    label={Text({
                      tid: "OTHER_INSTITUTE",
                      def: "Other Institute Name",
                    })}
                    rules={inputRequiredBasicRule}
                  >
                    <Input
                      disabled={disabled || isReviewMode}
                      placeholder={Text({
                        tid: "TEACHER_2026_PT1_Q18_D_OTHER_PLACEHOLDER",
                        def: "Ex. Avadhoota Datta Peetham, Mysore",
                      })}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
            {/* 18_E. Attached certificate no */}
            <div style={{ paddingLeft: "25px" }}>
              <DocumentUploadField
                fieldName='TEACHER_2026_PT1_Q18_E'
                label={
                <>
                  {Text({
                      tid: "TEACHER_2026_PT1_Q18_E",
                      def: "Attach Certificate",
                    })}
                    <FormHelpTooltip
                    title={Text({
                      tid: "TEACHER_2026_PT1_Q18_E_TOOLTIP",
                      def: "Upload the certificate file (PDF/JPG/PNG).",
                    })}
                  />
                </>
              }
              formId={formId || ""}
                userId={user?.id || ""}
                labelStyle={{ paddingLeft: "0" }}
                currentStage={eSTAGE.STAGE1}
                disabled={disabled || isReviewMode}
                existingDocuments={documentsData}
                onUploadComplete={(docId) =>
                  handleFieldChange(
                    "TEACHER_2026_PT1_Q18_E",
                    docId,
                    eAnswerType.FILE,
                  )
                }
              />
            </div>
          </div>
        </>
        {/* 19. Name of your Guru from whom you learnt and passed the above mentioned exam */}
        <Row>
          <Col
            sm={24}
            lg={12}
          >
            <Form.Item
              name='TEACHER_2026_PT1_Q19'
              label={Text({
                tid: "TEACHER_2026_PT1_Q19",
                def: "Name of your Guru from whom you learnt and passed the above mentioned exam",
              })}
              rules={inputRequiredBasicRule}
            >
              <Input
                disabled={disabled || isReviewMode}
                placeholder={Text({
                  tid: "TEACHER_2026_PT1_Q19_PLACEHOLDER",
                  def: "Ex. Vishwanath Sharma Guruji",
                })}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 20. If the above mentioned Veda teacher is your close relative, please provide details of the family relationship */}
        <>
          <Row>
            <Col
              xs={24}
              lg={24}
              xl={24}
            >
              <Form.Item
                name='TEACHER_2026_PT1_Q20_A'
                label={Text({
                  tid: "TEACHER_2026_PT1_Q20_A",
                  def: "Is your Guru your close relative?",
                })}
              >
                <Radio.Group disabled={disabled || isReviewMode}>
                  <Radio value='YES'>{Text({ tid: "YES", def: "Yes" })}</Radio>
                  <Radio value='NO'>{Text({ tid: "NO", def: "No" })}</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          {isGuruRelative === eYES_OR_NO.YES && (
            <Row>
              <Col sm={24}>
                <Form.Item
                  name='TEACHER_2026_PT1_Q20_B'
                  label={Text({
                    tid: "TEACHER_2026_PT1_Q20_B",
                    def: "If the above mentioned Veda teacher is your close relative, please provide details of the family relationship",
                  })}
                  rules={inputRequiredBasicRule}
                >
                  <>
                    <Input.TextArea
                      disabled={disabled || isReviewMode}
                      maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                      style={{ paddingBottom: 20 }}
                      placeholder={Text({
                        tid: "TEACHER_2026_PT1_Q20_B_PLACEHOLDER",
                        def: "Ex. Father",
                      })}
                    />
                  </>
                </Form.Item>
                <LengthCounter
                  form={form}
                  fieldName='TEACHER_2026_PT1_Q20_B'
                />
              </Col>
            </Row>
          )}
        </>
        {/* 21. Name of your Guru from whom you learnt and passed the above mentioned exam */}
        <>
          <Row>
            <Col
              xs={24}
              lg={24}
              xl={24}
            >
              <Form.Item
                name='TEACHER_2026_PT1_Q21_A'
                label={Text({
                  tid: "TEACHER_2026_PT1_Q21_A",
                  def: "Have you acquired any Ved knowledge that is considered rare or nearly extinct?",
                })}
              >
                <Radio.Group disabled={disabled || isReviewMode}>
                  <Radio value='YES'>{Text({ tid: "YES", def: "Yes" })}</Radio>
                  <Radio value='NO'>{Text({ tid: "NO", def: "No" })}</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          {isNearlyExtinct === eYES_OR_NO.YES && (
            <Row>
              <Col sm={24}>
                <Form.Item
                  name='TEACHER_2026_PT1_Q21_B'
                  label={Text({
                    tid: "TEACHER_2026_PT1_Q21_B",
                    def: "Have you acquired such knowledge which is considered to be extinct or rare? Give details. (Answer in maximum four lines)",
                  })}
                  rules={inputRequiredBasicRule}
                >
                  <>
                    <Input.TextArea
                      disabled={disabled || isReviewMode}
                      maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                      style={{ paddingBottom: 20 }}
                      placeholder={Q21_B_PLACEHOLDER}
                    />
                  </>
                </Form.Item>
                <LengthCounter
                  form={form}
                  fieldName='TEACHER_2026_PT1_Q21_B'
                />
              </Col>
            </Row>
          )}
        </>
        {/* 22) Description of your teachings of the Vedas in sequence: */}
        <Row>
          <Col
            xs={24}
            className='onClickRenderFields'
          >
            <Typography.Text>
              {Text({
                tid: "TEACHER_2026_PT1_Q22_HEADING",
                def: "1) Description of your teachings of the Vedas in sequence:",
              })}
            </Typography.Text>
            <Form.List name='TEACHER_2026_PT1_Q22'>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => {
                    return (
                      <Space
                        key={key}
                        align='baseline'
                        className='QuestionsSubSection'
                      >
                        <Row gutter={15}>
                          {/* From Date */}
                          <Col
                            sm={12}
                            lg={8}
                            xl={4}
                          >
                            <Form.Item
                              className='w-full'
                              {...restField}
                              name={[name, "TEACHER_2026_PT1_Q22_A1"]}
                              label={TEACHER_2026_PT1_Q22_A1}
                              rules={[
                                {
                                  required: true,
                                  message: REQUIRED,
                                },
                              ]}
                            >
                              <DatePicker
                                picker='month'
                                format='MM-YYYY'
                                placeholder={SELECT_DATE}
                                disabled={disabled || isReviewMode}
                                disabledDate={(current) =>
                                  current && current > dayjs().endOf("day")
                                }
                                style={{ width: "100%" }}
                              />
                            </Form.Item>
                          </Col>

                          {/* To Date */}
                          <Col
                            sm={12}
                            lg={8}
                            xl={4}
                          >
                            <Form.Item
                              className='w-full'
                              {...restField}
                              name={[name, "TEACHER_2026_PT1_Q22_A2"]}
                              label={TEACHER_2026_PT1_Q22_A2}
                              dependencies={[
                                [
                                  "TEACHER_2026_PT1_Q22",
                                  name,
                                  "TEACHER_2026_PT1_Q22_A1",
                                ],
                              ]}
                              rules={[
                                {
                                  required: true,
                                  message: REQUIRED,
                                },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    // Get From Date from the same form item
                                    const fromDate = getFieldValue([
                                      "TEACHER_2026_PT1_Q22",
                                      name,
                                      "TEACHER_2026_PT1_Q22_A1",
                                    ]);
                                    if (
                                      value &&
                                      fromDate &&
                                      value.isBefore(fromDate)
                                    ) {
                                      return Promise.reject(
                                        "To date cannot be before From date",
                                      );
                                    }
                                    return Promise.resolve();
                                  },
                                }),
                              ]}
                            >
                              <DatePicker
                                picker='month'
                                format='MM-YYYY'
                                placeholder={SELECT_DATE}
                                disabled={disabled || isReviewMode}
                                disabledDate={(current) =>
                                  current && current > dayjs().endOf("day")
                                }
                                style={{ width: "100%" }}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row>
                          <Col
                            sm={24}
                            lg={12}
                            xl={8}
                          >
                            <Form.Item
                              className='w-full'
                              {...restField}
                              name={[name, "TEACHER_2026_PT1_Q22_B"]}
                              label={TEACHER_2026_PT1_Q22_B}
                              rules={[
                                {
                                  required: true,
                                  message: REQUIRED,
                                },
                              ]}
                            >
                              <Input
                                disabled={disabled || isReviewMode}
                                placeholder={Q22_B_PLACEHOLDER}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row>
                          <Col
                            sm={24}
                            lg={12}
                            xl={8}
                          >
                            <Form.Item
                              className='w-full'
                              {...restField}
                              name={[name, "TEACHER_2026_PT1_Q22_C"]}
                              label={TEACHER_2026_PT1_Q22_C}
                              rules={[
                                {
                                  required: true,
                                  message: REQUIRED,
                                },
                              ]}
                            >
                              <Input
                                disabled={disabled || isReviewMode}
                                placeholder={Q22_C_PLACEHOLDER}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row>
                          <Col
                            sm={24}
                            lg={12}
                            xl={8}
                          >
                            <Form.Item
                              className='w-full'
                              {...restField}
                              name={[name, "TEACHER_2026_PT1_Q22_D"]}
                              label={TEACHER_2026_PT1_Q22_D}
                              rules={[
                                {
                                  required: true,
                                  message: REQUIRED,
                                },
                              ]}
                            >
                              <Select
                                disabled={disabled || isReviewMode}
                                showSearch
                                placeholder={Q22_D_PLACEHOLDER}
                                optionFilterProp='children'
                              >
                                {indianStateOptions.map((option) => (
                                  <Select.Option
                                    key={option.key}
                                    value={option.key}
                                  >
                                    {option.label}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row>
                          {/* Add Remove Button */}
                          <Col xs={24}>
                            <Flex gap={22}>
                              <Form.Item>
                                <Button
                                  variant='filled'
                                  className='actionButton'
                                  disabled={
                                    disabled || index === 0 || isReviewMode
                                  }
                                  // onClick={() => remove(name)}
                                  onClick={() => {
                                    // if (
                                    //   form.getFieldValue("TEACHER_PT1_Q24")
                                    //     ?.length > 1
                                    // ) {
                                    remove(name);
                                    // }
                                  }}
                                >
                                  {REMOVE}
                                </Button>
                              </Form.Item>
                              {form.getFieldValue("TEACHER_2026_PT1_Q22")
                                ?.length -
                                1 ===
                                index && (
                                <Form.Item>
                                  <Button
                                    disabled={disabled || isReviewMode}
                                    variant='filled'
                                    className='actionButton'
                                    onClick={() => {
                                      add();
                                    }}
                                    block
                                  >
                                    {ADD}
                                  </Button>
                                </Form.Item>
                              )}
                            </Flex>
                          </Col>
                        </Row>
                        {form.getFieldValue("TEACHER_2026_PT1_Q22")?.length -
                          1 >
                          index && <div className='divider'></div>}
                      </Space>
                    );
                  })}
                  {/* Initial Add button when no fields exist */}
                  {fields.length === 0 && !disabled && (
                    <Button
                      variant='filled'
                      className='actionButton'
                      onClick={() => add()}
                    >
                      {ADD}
                    </Button>
                  )}
                </>
              )}
            </Form.List>
          </Col>
        </Row>
        {/* 23). Name and full address of the present institution where you are teaching: */}
        <>
          <Row className=''>
            <Col sm={24}>
              <Form.Item
                name='TEACHER_2026_PT1_Q23'
                label={Text({
                  tid: "TEACHER_2026_PT1_Q23",
                  def: "Name and full address of the present institution where you are teaching",
                })}
                rules={inputRequiredBasicRule}
              >
                <>
                  <Input.TextArea
                    disabled={disabled || isReviewMode}
                    maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                    style={{ paddingBottom: 20 }}
                    placeholder={Text({
                      tid: "TEACHER_2026_PT1_Q23_PLACEHOLDER",
                      def: "Ex. Sanatan Vaidik Shikshan Sansthan, Kasar, Charbhuja, Rajsamand, Rajasthan, India-313332",
                    })}
                  />
                </>
              </Form.Item>
              <LengthCounter
                form={form}
                fieldName='TEACHER_2026_PT1_Q23'
              />
            </Col>
          </Row>
          {/* 23_A). Name of the head of the institution: */}
          <Row>
            <Col
              sm={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='TEACHER_2026_PT1_Q23_A'
                label={Text({
                  tid: "TEACHER_2026_PT1_Q23_A",
                  def: "Name of the head of the institution:",
                })}
                rules={inputRequiredBasicRule}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "TEACHER_2026_PT1_Q23_A_PLACEHOLDER",
                    def: "Ex. Umesh Dwivedi",
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
          {/* 23_B). Mobile number of the head of the institution: */}
          <Row>
            <Col
              sm={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='TEACHER_2026_PT1_Q23_B'
                label={Text({
                  tid: "TEACHER_2026_PT1_Q23_B",
                  def: "Mobile number of the head of the institution:",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                  {
                    pattern: Regex.MOBILENO, // Updated pattern
                    message: Text({
                      tid: "mobile_number_validation",
                      def: "Enter a valid 10-digit mobile number",
                    }),
                  },
                ]}
              >
                <Input
                  maxLength={10}
                  disabled={disabled || isReviewMode}
                  onKeyDown={allowOnlyNumericInput_onKeyDown}
                  placeholder={Text({
                    tid: "TEACHER_2026_PT1_Q23_B_PLACEHOLDER",
                    def: "Ex. 7357658777",
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
        </>
        {/* 24). Number of students currently studying Vedas with you: */}
        <>
          <Row>
            <Col
              xs={24}
              lg={12}
              xl={12}
            >
              <Typography.Text>
                {Text({
                  tid: "TEACHER_2026_PT1_Q24_HEADING",
                  def: "Number of students currently studying Vedas with you",
                })}
              </Typography.Text>
            </Col>
          </Row>
          <Row>
            <Col
              xs={24}
              lg={12}
              xl={6}
            >
              <Form.Item name='TEACHER_2026_PT1_Q24'>
                <Table<DataType>
                  columns={tableColumns}
                  size='large'
                  dataSource={TEACHER_2026_PT1_Q24}
                  pagination={false}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
        </>

        {/* 25). Details of students taught during his entire period of teaching Vedas: */}
        <>
          <Row>
            <Col>
              <Typography.Text>
                {Text({
                  tid: "TEACHER_2026_PT1_Q25",
                  def: "Details of students taught during his entire period of teaching Vedas",
                })}
              </Typography.Text>
            </Col>
          </Row>
          <Row>
            <Col
              xs={24}
              lg={12}
              xl={6}
            >
              <Form.Item name='TEACHER_2026_PT1_Q25'>
                <Table<DataType>
                  columns={tableColumns}
                  size='large'
                  dataSource={TEACHER_2026_PT1_Q25}
                  pagination={false}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
        </>

        {/* 26).  Number of your Veda students who left their studies midway without completing them */}
        <Row>
          <Col sm={24}>
            <Form.Item
              name='TEACHER_2026_PT1_Q26'
              label={Text({
                tid: "TEACHER_2026_PT1_Q26",
                def: " Number of your Veda students who left their studies midway without completing them ",
              })}
              rules={inputRequiredBasicRule}
            >
              <Input
                disabled={disabled || isReviewMode}
                placeholder={Text({
                  tid: "TEACHER_2026_PT1_Q26_PLACEHOLDER",
                  def: "00",
                })}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* 27). Efforts made for the study, teaching, preservation, and promotion of rare or nearly extinct branches of Ved or knowledge : */}
        <Row>
          <Col sm={24}>
            <Form.Item
              name='TEACHER_2026_PT1_Q27'
              label={Text({
                tid: "TEACHER_2026_PT1_Q27",
                def: "Efforts made for the study, teaching, preservation, and promotion of rare or nearly extinct branches of Ved or knowledge :",
              })}
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  disabled={disabled || isReviewMode}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                  style={{ paddingBottom: 20 }}
                  placeholder={Text({
                    tid: "TEACHER_2026_PT1_Q27_PLACEHOLDER",
                    def: "Ex. The study tradition of Shankhayana branch of Rigveda is almost extinct...",
                  })}
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='TEACHER_2026_PT1_Q27'
            />
          </Col>
        </Row>
        {/* 28). Have you made your close relatives study Vedas? If yes, their details:: */}
        {/* 28). Have you made your close relatives study Vedas? If yes, their details:: */}
        <Row>
          <Col
            xs={24}
            className='onClickRenderFields'
          >
            <Form.Item
              name='TEACHER_2026_PT1_Q28_YES_NO'
              label={Text({
                tid: "TEACHER_2026_PT1_Q28_HEADING",
                def: "Have you made your close relatives study Vedas? If yes, their details:",
              })}
            >
              <Radio.Group disabled={disabled || isReviewMode}>
                <Radio value='YES'>{Text({ tid: "YES", def: "Yes" })}</Radio>
                <Radio value='NO'>{Text({ tid: "NO", def: "No" })}</Radio>
              </Radio.Group>
            </Form.Item>

            {isRelativeTaught === eYES_OR_NO.YES && (
              <Form.List name='TEACHER_2026_PT1_Q28'>
                {(fields, { add, remove }) => (
                  <>
                    {fields.length === 0 && (
                      <Row>
                        <Col>
                          <Flex>
                            <Form.Item>
                              <Button
                                variant='filled'
                                className='actionButton'
                                disabled={disabled || isReviewMode}
                                onClick={() => {
                                  add();
                                }}
                                block
                              >
                                {ADD}
                              </Button>
                            </Form.Item>
                          </Flex>
                        </Col>
                      </Row>
                    )}
                    {fields.map(({ key, name, ...restField }, index) => {
                      return (
                        <Space
                          key={key}
                          align='baseline'
                          className='QuestionsSubSection'
                        >
                          <Row>
                            <Col
                              sm={24}
                              lg={12}
                              xl={8}
                            >
                              <Form.Item
                                className='w-full'
                                {...restField}
                                name={[name, "TEACHER_2026_PT1_Q28_A"]}
                                label={TEACHER_2026_PT1_Q28_A}
                                rules={[
                                  {
                                    required: true,
                                    message: REQUIRED,
                                  },
                                ]}
                              >
                                <Input
                                  disabled={disabled || isReviewMode}
                                  placeholder={Q28_A_PLACEHOLDER}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row>
                            <Col
                              sm={24}
                              lg={12}
                              xl={8}
                            >
                              <Form.Item
                                className='w-full'
                                {...restField}
                                name={[name, "TEACHER_2026_PT1_Q28_B"]}
                                label={TEACHER_2026_PT1_Q28_B}
                                rules={[
                                  {
                                    required: true,
                                    message: REQUIRED,
                                  },
                                ]}
                              >
                                <Input
                                  disabled={disabled || isReviewMode}
                                  placeholder={Q28_B_PLACEHOLDER}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row>
                            <Col
                              sm={24}
                              lg={12}
                              xl={8}
                            >
                              <Form.Item
                                className='w-full'
                                {...restField}
                                name={[name, "TEACHER_2026_PT1_Q28_C"]}
                                label={TEACHER_2026_PT1_Q28_C}
                                rules={[
                                  {
                                    required: true,
                                    message: REQUIRED,
                                  },
                                ]}
                              >
                                <Select
                                  disabled={disabled || isReviewMode}
                                  placeholder={DROPDOWN_PLACEHOLDER}
                                >
                                  <Select.Option value={MULANT}>
                                    {MULANT}
                                  </Select.Option>
                                  <Select.Option value={LEVEL_1}>
                                    {LEVEL_1}
                                  </Select.Option>
                                  <Select.Option value={LEVEL_2}>
                                    {LEVEL_2}
                                  </Select.Option>
                                  <Select.Option value={UCCHTAR}>
                                    {UCCHTAR}
                                  </Select.Option>
                                </Select>
                              </Form.Item>
                            </Col>
                          </Row>

                          <Row>
                            <Col
                              sm={24}
                              lg={12}
                              xl={8}
                            >
                              <Form.Item
                                className='w-full'
                                {...restField}
                                name={[name, "TEACHER_2026_PT1_Q28_D"]}
                                label={TEACHER_2026_PT1_Q28_D}
                                rules={[
                                  {
                                    required: true,
                                    message: REQUIRED,
                                  },
                                ]}
                              >
                                <Input
                                  disabled={disabled || isReviewMode}
                                  placeholder={Q28_D_PLACEHOLDER}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row>
                            {/* Add Remove Button */}
                            <Col xs={24}>
                              <Flex gap={22}>
                                <Form.Item>
                                  <Button
                                    variant='filled'
                                    className='actionButton'
                                    disabled={disabled || isReviewMode}
                                    // onClick={() => remove(name)}
                                    onClick={() => {
                                      remove(name);
                                    }}
                                  >
                                    {REMOVE}
                                  </Button>
                                </Form.Item>
                                {form.getFieldValue("TEACHER_2026_PT1_Q28")
                                  ?.length -
                                  1 ===
                                  index && (
                                  <Form.Item>
                                    <Button
                                      variant='filled'
                                      disabled={disabled || isReviewMode}
                                      className='actionButton'
                                      onClick={() => {
                                        add();
                                      }}
                                      block
                                    >
                                      {ADD}
                                    </Button>
                                  </Form.Item>
                                )}
                              </Flex>
                            </Col>
                          </Row>
                          {form.getFieldValue("TEACHER_2026_PT1_Q28")?.length -
                            1 >
                            index && <div className='divider'></div>}
                        </Space>
                      );
                    })}
                  </>
                )}
              </Form.List>
            )}
          </Col>
        </Row>
        {/* 29). Mention the main efforts made by you for the propagation and preservation of the Vedas. (Write the answer in maximum four lines): */}
        <Row>
          <Col sm={24}>
            <Form.Item
              name='TEACHER_2026_PT1_Q29'
              label={Text({
                tid: "TEACHER_2026_PT1_Q29",
                def: "Mention the main efforts made by you for the propagation and preservation of the Vedas. (Write the answer in maximum four lines)",
              })}
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  disabled={disabled || isReviewMode}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                  style={{ paddingBottom: 20 }}
                  placeholder={Text({
                    tid: "TEACHER_2026_PT1_Q29_PLACEHOLDER",
                    def: "Ex. Teaching Salakshana Ghananta, organizing Shalaka competitions...",
                  })}
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='TEACHER_2026_PT1_Q28'
            />
          </Col>
        </Row>
        {/* 30). Tell us five achievements of your teaching of Vedas that make you proud. (Write the answer in maximum five lines)*/}
        <Row>
          <Col sm={24}>
            <Form.Item
              name='TEACHER_2026_PT1_Q30'
              label={Text({
                tid: "TEACHER_2026_PT1_Q30",
                def: "Tell us five achievements of your teaching of Vedas that make you proud. (Write the answer in maximum five lines)",
              })}
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  disabled={disabled || isReviewMode}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                  style={{ paddingBottom: 20 }}
                  placeholder={Text({
                    tid: "TEACHER_2026_PT1_Q30_PLACEHOLDER",
                    def: "Ex. Created over a hundred Salakshana Ghananta teachers...",
                  })}
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='TEACHER_2026_PT1_Q30'
            />
          </Col>
        </Row>
        {/*31). Describe the qualities of an ideal Veda teacher. (Answer in maximum four lines): */}
        <Row>
          <Col sm={24}>
            <Form.Item
              name='TEACHER_2026_PT1_Q31'
              label={Text({
                tid: "TEACHER_2026_PT1_Q31",
                def: "Describe the qualities of an ideal Veda teacher. (Answer in maximum four lines)",
              })}
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  disabled={disabled || isReviewMode}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                  style={{ paddingBottom: 20 }}
                  placeholder={Text({
                    tid: "TEACHER_2026_PT1_Q31_PLACEHOLDER",
                    def: "Ex. Practicing conduct prescribed in Vedas...",
                  })}
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='TEACHER_2026_PT1_Q31'
            />
          </Col>
        </Row>
        {/*32). Today's young generation does not understand the importance of Vedas in their daily lives. How will you explain the importance of Vedas to such people? (Write the answer in maximum four lines*/}
        <Row>
          <Col sm={24}>
            <Form.Item
              name='TEACHER_2026_PT1_Q32'
              label={Text({
                tid: "TEACHER_2026_PT1_Q32",
                def: "Today's young generation does not understand the importance of Vedas in their daily lives. How will you explain the importance of Vedas to such people? (Write the answer in maximum four lines)",
              })}
              rules={inputRequiredBasicRule}
            >
              <>
                <Input.TextArea
                  disabled={disabled || isReviewMode}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                  style={{ paddingBottom: 20 }}
                  placeholder={Text({
                    tid: "TEACHER_2026_PT1_Q32_PLACEHOLDER",
                    def: "Ex. Presenting positive changes...",
                  })}
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='TEACHER_2026_PT1_Q32'
            />
          </Col>
        </Row>
        {/* 28) TEACHER_PT1_Q36_AFFIDAVIT_TITLE */}
        <div
          id='tch_form1_teacher_affidavit'
          className='affidavitSection'
        >
          <Row>
            <Col>
              <Typography.Text>
                {Text({
                  tid: "TEACHER_2026_PT1_AFFIDAVIT_TITLE",
                  def: "TEACHER_2026_PT1_AFFIDAVIT_TITLE",
                })}
              </Typography.Text>
            </Col>
          </Row>

          {/* 29) affidavit declaration */}
          <Row className=' affidavitRow'>
            <Col sm={24}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginBottom: "var(--bs-label-bottom-margin)",
                }}
              >
                {Text({
                  tid: "TEACHER_2026_PT1_AFFIDAVIT_DECLARATION_1",
                  def: "I,",
                })}
                <Form.Item
                  name='TEACHER_2026_PT1_Q33'
                  rules={inputRequiredBasicRule}
                  className='affidavitRowInput'
                >
                  <Input
                    disabled={true}
                    onBlur={(e) =>
                      handleFieldChange("TEACHER_2026_PT1_Q33", e.target.value)
                    }
                  />
                </Form.Item>
                {Text({
                  tid: "TEACHER_2026_PT1_AFFIDAVIT_DECLARATION_2",
                  def: "solemnly declare that:",
                })}
              </div>
              <ul className='mb-label'>
                <li>
                  <Typography.Text>
                    {Text({
                      tid: "TEACHER_2026_PT1_AFFIDAVIT_VED_TEACHER",
                      def: "I am currently a full-time Ved teacher.",
                    })}
                  </Typography.Text>
                </li>
                <li>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    {Text({
                      tid: "TEACHER_2026_PT1_AFFIDAVIT_YEARS_TEACHING_1",
                      def: "I have been continuously engaged in full-time Ved teaching for the past __ years.",
                    })}
                    <Form.Item
                      name='TEACHER_2026_PT1_Q34'
                      className=' affidavitRowInput'
                      rules={numberInputRules({
                        required: true,
                        allowNegative: false,
                        min: 0,
                        max: 120,
                      })}
                    >
                      <Input
                        disabled={disabled || isReviewMode}
                        id=''
                      />
                    </Form.Item>
                    {Text({
                      tid: "TEACHER_2026_PT1_AFFIDAVIT_YEARS_TEACHING_2",
                      def: "I have been continuously engaged in full-time Ved teaching for the past __ years.",
                    })}
                  </div>
                </li>
                <li>
                  <Typography.Text type='secondary'>
                    {Text({
                      tid: "TEACHER_2026_PT1_AFFIDAVIT_INFORMATION_ACCURACY",
                      def: "All the information provided in this application form is completely true.",
                    })}
                  </Typography.Text>
                </li>

                <li>
                  <Typography.Text type='secondary'>
                    {Text({
                      tid: "TEACHER_2026_PT1_AFFIDAVIT_DOCUMENTS_AUTHENTICITY",
                      def: "All the information in the attached documents is true.",
                    })}
                  </Typography.Text>
                </li>
              </ul>
              <Typography.Text>
                {Text({
                  tid: "TEACHER_2026_PT1_AFFIDAVIT_SIGN_CONFIRMATION",
                  def: "I have signed this affidavit after verifying all the facts.",
                })}
              </Typography.Text>
              <Typography.Text>
                {Text({
                  tid: "TEACHER_2026_PT1_AFFIDAVIT_UNDERSTANDING",
                  def: "I fully understand the significance of signing this affidavit.",
                })}
              </Typography.Text>

              {!disabled ? (
                <Form.Item
                  name='affidavitAccepted'
                  valuePropName='checked'
                  rules={[
                    {
                      required: true,
                      transform: (value: boolean) => value || undefined,
                      type: "boolean",
                      message: Text({
                        tid: "AFFIDAVIT_ACCEPTANCE_REQUIRED",
                        def: "You must accept the affidavit declaration to submit the form",
                      }),
                    },
                  ]}
                  style={{ marginBottom: 0, marginTop: 16 }}
                >
                  <Checkbox
                    checked={affidavitAccepted}
                    disabled={disabled || isReviewMode}
                  >
                    {Text({
                      tid: "AFFIDAVIT_ACCEPTANCE",
                      def: "I accept the above affidavit declaration",
                    })}
                  </Checkbox>
                </Form.Item>
              ) : (
                <div style={{ marginTop: 16 }}>
                  <Form.Item
                    name='affidavitAccepted'
                    valuePropName='checked'
                    style={{ marginBottom: 0 }}
                  >
                    <Checkbox
                      disabled={true}
                      checked={affidavitAccepted}
                    >
                      {Text({
                        tid: "AFFIDAVIT_ACCEPTANCE",
                        def: "I accept the above affidavit declaration",
                      })}
                    </Checkbox>
                  </Form.Item>
                </div>
              )}
            </Col>
          </Row>
          {/* 32) full name */}
          <Row className='QuestionsSubSection affidavitQuestionFirst'>
            <Col
              sm={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='TEACHER_2026_PT1_Q35'
                label={Text({
                  tid: "TEACHER_2026_PT1_FULL_NAME",
                  def: "Full Name",
                })}
                rules={inputRequiredBasicRule}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Q35_PLACEHOLDER}
                />
              </Form.Item>
            </Col>
          </Row>
          {/* 31) Teacher location*/}
          <Row className='QuestionsSubSection'>
            <Col
              sm={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='TEACHER_2026_PT1_Q36'
                label={Text({
                  tid: "TEACHER_2026_PT1_PLACE",
                  def: "Ex. Ujjain",
                })}
                rules={inputRequiredBasicRule}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Q36_PLACEHOLDER}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* 33) date */}
          <Row className='QuestionsSubSection'>
            <Col
              sm={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='TEACHER_2026_PT1_Q37'
                label={Text({
                  tid: "TEACHER_2026_PT1_DATE",
                  def: "Date",
                })}
                rules={inputRequiredBasicRule}
              >
                <DatePicker
                  format='DD-MM-YYYY'
                  disabled={disabled || isReviewMode}
                  disabledDate={(current) =>
                    current && current > dayjs().endOf("day")
                  }
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className=' QuestionsSubSection'>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              <DocumentUploadField
                fieldName='TEACHER_2026_PT1_Q38'
                label={
                  <>
                    {Text({
                      tid: "SIGNATURE_OF_APPLICANT",
                      def: "Signature of Applicant",
                    })}
                    <FormHelpTooltip
                      title={Text({
                        tid: "SCHOOL_2026_PT1_Q23_TOOLTIP",
                        def: "Please upload a scanned copy or clear photograph of the signature of the Head of the Institution. The signature must be clearly visible and legible. Accepted file formats are PDF, JPG, and PNG.",
                      })}
                    />
                  </>
                }

                formId={formId || ""}
                userId={user?.id || ""}
                currentStage={eFORMSTATUS.STAGE1INPROGRESS}
                disabled={disabled || isReviewMode}
                existingDocuments={documentsData}
                onUploadComplete={(docId) =>
                  handleFieldChange(
                    "TEACHER_2026_PT1_Q38",
                    docId,
                    eAnswerType.FILE,
                  )
                }
              />
            </Col>
          </Row>
        </div>

        {/* Button Submit */}
        <Row
          style={
            disabled === true
              ? { marginTop: "var(--bs-item-negative-margin-30)" }
              : undefined
          }
        >
          {disabled === false && !isReviewMode && (
            <Button
              block
              type='primary'
              onClick={handleNextClick}
              disabled={isLoading || !affidavitAccepted}
              className='formSubmitButton'
            >
              {Text({ tid: "NEXT", def: "Next" })}
            </Button>
          )}
          {disabled === false && isReviewMode && (
            <div style={{ display: "flex", gap: "16px", width: "100%" }}>
              <Button
                block
                onClick={handleBackClick}
                disabled={isLoading}
                className='formSubmitButton'
              >
                {Text({ tid: "BACK", def: "Back" })}
              </Button>
              <Button
                block
                type='primary'
                htmlType='submit'
                disabled={isLoading || !affidavitAccepted}
                className='formSubmitButton'
                loading={isSubmitting}
              >
                {Text({ tid: "SUBMIT", def: "Submit" })}
              </Button>
            </div>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default TeacherPartOneCurrent;
